import React from 'react';
import { useCountUp } from '@/hooks/useCountUp';

export default function Hero({ stats }) {
    const milestones = stats?.total_milestones ?? 0;
    const phases = stats?.phases_available ?? 7;
    // count-up trigger keyed on the actual value so it re-runs when stats load
    const animatedMilestones = useCountUp(milestones, { duration: 1600, trigger: milestones });
    const animatedPhases = useCountUp(phases, { duration: 1200, trigger: phases });
    const animatedApproved = useCountUp(100, { duration: 1400, trigger: 'approved' });
    return (
        <section
            data-testid="hero-section"
            className="relative overflow-hidden border-b border-white/10 bg-black grain"
        >
            {/* animated color blobs */}
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-0">
                <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-un-berry/40 blur-[120px] animate-blob" />
                <div className="absolute top-20 right-0 h-[380px] w-[380px] rounded-full bg-un-pumpkin/30 blur-[110px] animate-blob" style={{ animationDelay: '3s' }} />
                <div className="absolute -bottom-20 left-1/3 h-[360px] w-[360px] rounded-full bg-un-lime/25 blur-[120px] animate-blob" style={{ animationDelay: '6s' }} />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-32">
                {/* Eyebrow */}
                <div className="flex items-center gap-4 mb-8 animate-fade-up">
                    <img
                        src="/uncloned-logo.webp"
                        alt="UnCloned®"
                        data-testid="hero-eyebrow-logo"
                        className="h-8 w-8 object-contain shrink-0"
                    />
                    <span className="text-xs md:text-sm uppercase tracking-[0.32em] text-white/70 font-semibold">
                        2026 · Recipient Directory
                    </span>
                </div>

                <h1
                    data-testid="hero-title"
                    aria-label="UnCloned® Brand Awards"
                    className="hero-logo-mask max-w-3xl w-full animate-fade-up"
                    style={{
                        animationDelay: '80ms',
                        WebkitMaskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                        maskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                    }}
                />
                <span className="sr-only">UnCloned® Brand Awards</span>

                <div
                    className="mt-8 md:mt-10 max-w-2xl text-white/80 font-body text-base md:text-lg leading-relaxed animate-fade-up"
                    style={{ animationDelay: '160ms' }}
                >
                    <p>
                        Congratulations to every client who participated in the
                        <span className="text-white font-bold"> UnCloned<span className="text-white">®</span> Brand Vault </span>
                        and completed the required milestones to earn an
                        <span className="text-white font-bold"> UnCloned<span className="text-white">®</span> Brand Award</span>.
                    </p>
                    <p className="mt-4">
                        Collectively, this year&apos;s awardees represent
                        <span data-testid="hero-total-milestones" className="text-un-lime font-semibold"> {milestones} completed milestone{milestones === 1 ? '' : 's'} </span>
                        across the
                        <span className="text-white font-bold"> UnCloned<span className="text-white">®</span> Brand Framework </span>
                        — each one reviewed, validated, and approved as part of their journey.
                    </p>
                </div>

                {/* stat strip */}
                <div
                    className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl animate-fade-up"
                    style={{ animationDelay: '240ms' }}
                >
                    <StatChip label="Milestones Completed" value={animatedMilestones} color="#D0FF0D" />
                    <StatChip label="Phases" value={animatedPhases} color="#0FFFE2" />
                    <StatChip label="Reviewed & approved" value={`${animatedApproved}%`} color="#FC861E" />
                </div>
            </div>
        </section>
    );
}

function StatChip({ label, value, color }) {
    return (
        <div
            className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur-md px-5 py-4"
            data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="text-xs uppercase tracking-[0.22em] text-white/60">{label}</div>
            <div
                className="mt-1 font-display font-black text-3xl tabular-nums"
                style={{ color }}
            >
                {value}
            </div>
        </div>
    );
}
